import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getData, serverURL } from "../../services/FetchNodeservices";
import { FaArrowLeft, FaPrint } from "react-icons/fa";

export default function CandidateSummary() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editable candidate fields state
  const [fields, setFields] = useState({
    name: "",
    contactNo: "",
    email: "",
    maritalStatus: "",
    dob: "",
    gender: "",
    roleAppliedFor: "",
    reportingTo: "Manager",
    reportees: "Members",
    revenueAccountability: "",
    
    // PG
    pgDegree: "",
    pgInstitute: "",
    pgYear: "",
    pgScore: "",
    pgTarget: "",
    pgTime: "",

    // UG
    ugDegree: "B.Sc",
    ugInstitute: "Fergusson College, Pune",
    ugYear: "",
    ugScore: "",
    ugTarget: "",
    ugTime: "",

    tenthPercent: "",
    twelfthPercent: "",

    totalExp: "",
    currentOrg: "",
    tenureCurrentOrg: "",
    currentDesignation: "",

    currentLocation: "",
    locationAppliedFor: "",
    nativeLocation: "",
    otherLocations: "",

    currentCtc: "",
    expectedCtc: "",
    noticePeriod: "",
    reasonForChange: "",

    appraisalDate: "",
    lastAppraisalPercent: "",
    lastBonus: "",
    promotionDue: "",

    revAccountabilityShared: "",
    targetOrg: "",
    jdShared: "",

    // Recruiter comments section
    cibilScore: "",
    cibilOverdue: "",
    jdMatch: "",
    communicationSkills: "",
    technicalSkills: "",
    reasonForLoading: "",

    // Compulsory Eligibility Table Remarks
    eduRemarks: "",
    workExpRemarks: "",
    assetSalesRemarks: "",
    altSalesRemarks: "",
    teamMgmtRemarks: "",
    channelMgmtRemarks: "",
  });

  const parseQualifications = (qualStr) => {
    if (!qualStr || typeof qualStr !== "string") {
      return { ugDegree: "", pgDegree: "" };
    }

    const str = qualStr.trim();
    if (!str) return { ugDegree: "", pgDegree: "" };

    const isPgDegree = (item) => {
      const s = item.trim().toLowerCase();
      return (
        /\b(mba|m\.?tech|m\.?e|m\.?sc|m\.?com|m\.?a|m\.?s|m\.?pharma|m\.?ed|m\.?des|mca|ll\.?m|pgdm|pgdca|pgdbm|pgd|pg)\b/i.test(s) ||
        s.includes("master") ||
        s.includes("post grad") ||
        s.includes("post-grad")
      );
    };

    const isUgDegree = (item) => {
      const s = item.trim().toLowerCase();
      return (
        /\b(b\.?tech|b\.?e|b\.?sc|b\.?com|b\.?a|b\.?s|b\.?pharma|b\.?ed|b\.?des|bca|bba|ll\.?b|b\.?arch|ug)\b/i.test(s) ||
        s.includes("bachelor") ||
        s.includes("graduat")
      );
    };

    const tokens = str.split(/[,/|&+]|\band\b/i).map((t) => t.trim()).filter(Boolean);

    let pgDegree = "";
    let ugDegree = "";

    if (tokens.length > 1) {
      const pgs = [];
      const ugs = [];
      tokens.forEach((t) => {
        if (isPgDegree(t)) {
          pgs.push(t);
        } else if (isUgDegree(t)) {
          ugs.push(t);
        } else {
          ugs.length === 0 ? ugs.push(t) : pgs.push(t);
        }
      });
      pgDegree = pgs.join(", ");
      ugDegree = ugs.join(", ");
    } else {
      if (isPgDegree(str)) {
        pgDegree = str;
        ugDegree = "";
      } else {
        ugDegree = str;
        pgDegree = "";
      }
    }

    return { ugDegree, pgDegree };
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const res = await getData(`candidate/details/${id}`);
        if (res.status && res.data) {
          const d = res.data;
          const { ugDegree, pgDegree } = parseQualifications(d.qualification || "");
          setFields((prev) => ({
            ...prev,
            name: `${d.f_name || ""} ${d.l_name || ""}`.trim() || d.candidate_name || "",
            contactNo: d.phone_number || "",
            email: d.email_id || "",
            roleAppliedFor: d.job_title || d.designation || "",
            totalExp: d.experience || "",
            currentOrg: d.current_company || "",
            currentDesignation: d.designation || "",
            currentLocation: d.hometown || "",
            currentCtc: d.current_ctc || "",
            expectedCtc: d.expected_ctc || "",
            noticePeriod: d.notice_period || "",
            ugDegree: ugDegree,
            pgDegree: pgDegree,
          }));
        } else {
          setError("Candidate details not found.");
        }
      } catch (err) {
        console.error("fetchDetails error:", err);
        setError("Failed to fetch candidate details.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const handleChange = (key, value) => {
    setFields((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-xs text-gray-500 font-bold uppercase tracking-wider">
        <svg className="w-10 h-10 text-indigo-600 animate-spin mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <circle className="opacity-25" cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={4} />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        Loading Candidate Summary Sheet...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-xs text-red-500 font-bold uppercase tracking-wider gap-3">
        <span>{error}</span>
        <button onClick={() => navigate(-1)} className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-bold">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100/30 p-4 sm:p-8 font-sans antialiased text-gray-900 print:bg-white print:p-0">
      
      {/* Top action controls bar */}
      <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between no-print bg-white p-4 rounded-3xl border border-gray-100 shadow-sm">
        <button
          onClick={() => window.close()}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-xl text-xs font-bold transition duration-150 cursor-pointer"
        >
          <FaArrowLeft className="w-3.5 h-3.5" /> Close Tab
        </button>
        
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 bg-slate-100 px-3 py-1.5 rounded-lg border border-gray-200/40">
            Click cells to edit values inline
          </span>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition duration-150 shadow-md cursor-pointer"
          >
            <FaPrint className="w-4 h-4" /> Download / Print PDF
          </button>
        </div>
      </div>

      {/* Main Print Container Sheet */}
      <div className="max-w-4xl mx-auto bg-white p-8 border border-gray-200 shadow-2xl rounded-3xl print:border-none print:shadow-none print:p-0 print:m-0 print:rounded-none">
        
        {/* Style block specifically designed to support clean printing */}
        <style dangerouslySetInnerHTML={{ __html: `
          @media print {
            body {
              background: white !important;
              color: black !important;
              font-size: 11px !important;
            }
            .no-print {
              display: none !important;
            }
            input, textarea {
              border: none !important;
              outline: none !important;
              background: transparent !important;
              padding: 0 !important;
              margin: 0 !important;
              box-shadow: none !important;
              font-size: inherit !important;
              font-family: inherit !important;
              font-weight: inherit !important;
              color: black !important;
              resize: none !important;
            }
            td {
              border: 1px solid #000000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            th {
              border: 1px solid #000000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            table {
              border-collapse: collapse !important;
              border: 1px solid #000000 !important;
            }
          }
          /* Custom cell editor input styling */
          .cell-input {
            width: 100%;
            border: none;
            outline: none;
            background: transparent;
            font-size: 11px;
            font-family: inherit;
            text-align: inherit;
            color: #1e293b;
            padding: 4px 6px;
            font-weight: 600;
          }
          .cell-input:hover {
            background-color: rgba(99, 102, 241, 0.04);
          }
          .cell-input:focus {
            background-color: rgba(99, 102, 241, 0.08);
            box-shadow: inset 0 0 0 1px #818cf8;
          }
          /* Explicit PDF border rules */
          .pdf-table {
            border-collapse: collapse;
            width: 100%;
            border: 1.5px solid #000000;
          }
          .pdf-table td {
            border: 1.5px solid #000000;
            padding: 0; /* Let input take space */
            font-size: 11px;
            font-family: sans-serif;
            vertical-align: middle;
          }
          .pdf-label {
            font-weight: bold;
            font-size: 11px;
            color: #000000;
            padding: 6px 8px;
            text-align: center;
          }
        ` }} />

        {/* SUMMARY SHEET TOP TABLE */}
        <table className="pdf-table w-full">
          <tbody>
            
            {/* Table main header */}
            <tr>
              <td colSpan={8} className="pdf-label font-extrabold text-sm uppercase py-2 bg-slate-50">
                Summary Sheet
              </td>
            </tr>

            {/* Row 1: Name / Contact */}
            <tr>
              <td colSpan={1} className="pdf-label w-[15%]">Name</td>
              <td colSpan={3} className="w-[35%]">
                <input
                  type="text"
                  className="cell-input text-left"
                  value={fields.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Enter Name"
                />
              </td>
              <td colSpan={2} className="pdf-label w-[20%]">Contact No.</td>
              <td colSpan={2} className="w-[30%]">
                <input
                  type="text"
                  className="cell-input text-left"
                  value={fields.contactNo}
                  onChange={(e) => handleChange("contactNo", e.target.value)}
                  placeholder="Enter Contact"
                />
              </td>
            </tr>

            {/* Row 2: Email / Marital / DOB / Gender */}
            <tr>
              <td colSpan={1} className="pdf-label">Email ID</td>
              <td colSpan={2} className="w-[30%]">
                <input
                  type="text"
                  className="cell-input text-left"
                  value={fields.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="Enter Email"
                />
              </td>
              <td colSpan={1} className="pdf-label w-[15%]">Marital Status:</td>
              <td colSpan={1} className="w-[12%]">
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.maritalStatus}
                  onChange={(e) => handleChange("maritalStatus", e.target.value)}
                />
              </td>
              <td colSpan={1} className="pdf-label w-[15%] text-[10px]">DOB:(dd/mm/yyyy)</td>
              <td colSpan={1} className="w-[12%]">
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.dob}
                  onChange={(e) => handleChange("dob", e.target.value)}
                  placeholder="dd/mm/yyyy"
                />
              </td>
              <td colSpan={1} className="w-[15%]">
                <div className="flex items-center justify-between text-[11px] font-bold px-1 gap-1">
                  <span>Gender:F/M</span>
                  <input
                    type="text"
                    className="w-8 border-none outline-none text-center bg-transparent font-extrabold"
                    value={fields.gender}
                    onChange={(e) => handleChange("gender", e.target.value)}
                  />
                </div>
              </td>
            </tr>

            {/* Row 3: Role / Reporting */}
            <tr>
              <td colSpan={2} className="pdf-label">Role Applied for</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.roleAppliedFor}
                  onChange={(e) => handleChange("roleAppliedFor", e.target.value)}
                />
              </td>
              <td colSpan={2} className="pdf-label">Reporting to (Designation)</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.reportingTo}
                  onChange={(e) => handleChange("reportingTo", e.target.value)}
                />
              </td>
            </tr>

            {/* Row 4: Reportees / Revenue Accountability */}
            <tr>
              <td colSpan={2} className="pdf-label">Reportees (Designation)</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.reportees}
                  onChange={(e) => handleChange("reportees", e.target.value)}
                />
              </td>
              <td colSpan={2} className="pdf-label">Revenue accountability (if applicable)</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.revenueAccountability}
                  onChange={(e) => handleChange("revenueAccountability", e.target.value)}
                />
              </td>
            </tr>

            {/* PG Headers */}
            <tr style={{ backgroundColor: "#d9ead3" }}>
              <td colSpan={2} className="pdf-label font-bold text-center">Post-Graduation</td>
              <td colSpan={2} className="pdf-label font-bold text-center">Name of Institute</td>
              <td colSpan={2} className="pdf-label font-bold text-center">Year (From -To)</td>
              <td colSpan={2} className="pdf-label font-bold text-center">Score</td>
            </tr>

            {/* PG details inputs */}
            <tr style={{ backgroundColor: "#e2f0d9" }}>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.pgDegree}
                  onChange={(e) => handleChange("pgDegree", e.target.value)}
                  placeholder="Degree name"
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.pgInstitute}
                  onChange={(e) => handleChange("pgInstitute", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.pgYear}
                  onChange={(e) => handleChange("pgYear", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.pgScore}
                  onChange={(e) => handleChange("pgScore", e.target.value)}
                />
              </td>
            </tr>

            {/* PG sub questions */}
            <tr style={{ backgroundColor: "#e2f0d9" }}>
              <td colSpan={2} className="pdf-label text-left font-bold text-[10px]">Whether from Target Institute (Y/N) :</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.pgTarget}
                  onChange={(e) => handleChange("pgTarget", e.target.value)}
                />
              </td>
              <td colSpan={2} className="pdf-label text-left font-bold text-[10px]">Full Time /Part Time ( F/P) :</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.pgTime}
                  onChange={(e) => handleChange("pgTime", e.target.value)}
                />
              </td>
            </tr>

            {/* UG Headers */}
            <tr style={{ backgroundColor: "#d9ead3" }}>
              <td colSpan={2} className="pdf-label font-bold text-center">Graduation</td>
              <td colSpan={2} className="pdf-label font-bold text-center">Name of Institute</td>
              <td colSpan={2} className="pdf-label font-bold text-center">Year (From -To)</td>
              <td colSpan={2} className="pdf-label font-bold text-center">Score</td>
            </tr>

            {/* UG details inputs */}
            <tr style={{ backgroundColor: "#e2f0d9" }}>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.ugDegree}
                  onChange={(e) => handleChange("ugDegree", e.target.value)}
                  placeholder="Degree name"
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.ugInstitute}
                  onChange={(e) => handleChange("ugInstitute", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.ugYear}
                  onChange={(e) => handleChange("ugYear", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.ugScore}
                  onChange={(e) => handleChange("ugScore", e.target.value)}
                />
              </td>
            </tr>

            {/* UG sub questions */}
            <tr style={{ backgroundColor: "#e2f0d9" }}>
              <td colSpan={2} className="pdf-label text-left font-bold text-[10px]">Whether from Target Institute (Y/N) :</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.ugTarget}
                  onChange={(e) => handleChange("ugTarget", e.target.value)}
                />
              </td>
              <td colSpan={2} className="pdf-label text-left font-bold text-[10px]">Full Time /Part Time ( F/P) :</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.ugTime}
                  onChange={(e) => handleChange("ugTime", e.target.value)}
                />
              </td>
            </tr>

            {/* 10th and 12th headers */}
            <tr>
              <td colSpan={4} className="pdf-label font-bold text-center bg-slate-50">10th %</td>
              <td colSpan={4} className="pdf-label font-bold text-center bg-slate-50">12th %</td>
            </tr>
            <tr>
              <td colSpan={4}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.tenthPercent}
                  onChange={(e) => handleChange("tenthPercent", e.target.value)}
                />
              </td>
              <td colSpan={4}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.twelfthPercent}
                  onChange={(e) => handleChange("twelfthPercent", e.target.value)}
                />
              </td>
            </tr>

            {/* Experience / Org headers */}
            <tr className="bg-slate-50">
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Total Experience (in Years)</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Current Organization</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Tenure in Current Organization ( in Years)</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Current Designation</td>
            </tr>
            <tr>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.totalExp}
                  onChange={(e) => handleChange("totalExp", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.currentOrg}
                  onChange={(e) => handleChange("currentOrg", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.tenureCurrentOrg}
                  onChange={(e) => handleChange("tenureCurrentOrg", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.currentDesignation}
                  onChange={(e) => handleChange("currentDesignation", e.target.value)}
                />
              </td>
            </tr>

            {/* Location headers */}
            <tr className="bg-slate-50">
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Current Location</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Location Applied for</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Native location</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[9px]">Capture other locations s/he might be interested in</td>
            </tr>
            <tr>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.currentLocation}
                  onChange={(e) => handleChange("currentLocation", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.locationAppliedFor}
                  onChange={(e) => handleChange("locationAppliedFor", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.nativeLocation}
                  onChange={(e) => handleChange("nativeLocation", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.otherLocations}
                  onChange={(e) => handleChange("otherLocations", e.target.value)}
                />
              </td>
            </tr>

            {/* CTC headers */}
            <tr className="bg-slate-50">
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Current CTC</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Expected CTC ( Mandatory) or % increase</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Notice Period</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Reason for Job Change</td>
            </tr>
            <tr>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.currentCtc}
                  onChange={(e) => handleChange("currentCtc", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.expectedCtc}
                  onChange={(e) => handleChange("expectedCtc", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.noticePeriod}
                  onChange={(e) => handleChange("noticePeriod", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.reasonForChange}
                  onChange={(e) => handleChange("reasonForChange", e.target.value)}
                />
              </td>
            </tr>

            {/* Appraisal headers */}
            <tr style={{ backgroundColor: "#fce4d6" }}>
              <td colSpan={2} className="pdf-label font-bold text-center text-[9px]">Appraisal received Date (mention due if applicable)</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Last Appraisal % (%)</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Last Bonus received</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Promotion Due (if any) ( Y/N)</td>
            </tr>
            <tr style={{ backgroundColor: "#fce4d6" }}>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.appraisalDate}
                  onChange={(e) => handleChange("appraisalDate", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.lastAppraisalPercent}
                  onChange={(e) => handleChange("lastAppraisalPercent", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.lastBonus}
                  onChange={(e) => handleChange("lastBonus", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.promotionDue}
                  onChange={(e) => handleChange("promotionDue", e.target.value)}
                />
              </td>
            </tr>

            {/* JD / Target list rows */}
            <tr style={{ backgroundColor: "#fff2cc" }}>
              <td colSpan={3} className="pdf-label text-left font-bold text-[9px] leading-tight">
                Whether revenue accountability of proposed role has been shared or not :- <br/>(Y/N)
              </td>
              <td colSpan={3} className="pdf-label text-left font-bold text-[9px] leading-tight">
                Whether is from Target list of Organizations? : <br/>( Y/N)
              </td>
              <td colSpan={2} className="pdf-label text-left font-bold text-[9px] leading-tight">
                Whether the JD has been shared or not :- <br/>(Y/N)
              </td>
            </tr>
            <tr style={{ backgroundColor: "#fff2cc" }}>
              <td colSpan={3}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.revAccountabilityShared}
                  onChange={(e) => handleChange("revAccountabilityShared", e.target.value)}
                />
              </td>
              <td colSpan={3}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.targetOrg}
                  onChange={(e) => handleChange("targetOrg", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center"
                  value={fields.jdShared}
                  onChange={(e) => handleChange("jdShared", e.target.value)}
                />
              </td>
            </tr>

          </tbody>
        </table>

        {/* RECRUITER COMMENTS SECTION */}
        <div className="mt-5 text-[11px] font-semibold text-black space-y-3.5">
          <div>
            <h4 className="font-extrabold text-[12px] underline">Recruiter Comments:-</h4>
            <p className="font-extrabold mt-1 text-[11px]">
              Please mention your assessment of Profile matching with the Job Description in terms of :-
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <span className="font-bold whitespace-nowrap">1. CIBIL Score –</span>
              <input
                type="text"
                className="border-b border-black outline-none font-bold px-1.5 flex-1 max-w-[200px]"
                value={fields.cibilScore}
                onChange={(e) => handleChange("cibilScore", e.target.value)}
              />
            </div>

            <div className="flex items-center gap-1">
              <span className="font-bold whitespace-nowrap">2. CIBIL Overdue Amount -</span>
              <input
                type="text"
                className="border-b border-black outline-none font-bold px-1.5 flex-1 max-w-[200px]"
                value={fields.cibilOverdue}
                onChange={(e) => handleChange("cibilOverdue", e.target.value)}
              />
            </div>

            <div className="flex items-center gap-1">
              <span className="font-bold whitespace-nowrap">3. Match of Profile with the JD ( Yes/No):-</span>
              <input
                type="text"
                className="border-b border-black outline-none font-bold px-1.5 w-24 text-center"
                value={fields.jdMatch}
                onChange={(e) => handleChange("jdMatch", e.target.value)}
              />
            </div>

            <div className="flex items-center gap-1">
              <span className="font-bold whitespace-nowrap">4. Communication Skills ( Spoken English and Articulation ) ( High /Low):-</span>
              <input
                type="text"
                className="border-b border-black outline-none font-bold px-1.5 w-28 text-center"
                value={fields.communicationSkills}
                onChange={(e) => handleChange("communicationSkills", e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1 mt-3">
              <span className="font-bold">
                5. Technical Skills ( Whether technical requirements mapped in JD are found in the profile) ( Mention what technical skills found):-Reason for loading the CV against the job requisition :-
              </span>
              <textarea
                rows={3}
                className="border border-black outline-none p-2 w-full font-semibold rounded-lg focus:ring-1 focus:ring-indigo-500 mt-1"
                value={fields.technicalSkills}
                onChange={(e) => handleChange("technicalSkills", e.target.value)}
                placeholder="Type details here..."
              />
            </div>
          </div>
        </div>

        {/* COMPULSORY ELIGIBILITY CRITERIA TABLE */}
        <div className="mt-8">
          <table className="pdf-table w-full">
            <thead>
              <tr style={{ backgroundColor: "#f2f2f2" }}>
                <th colSpan={3} className="pdf-label font-extrabold text-[12px] uppercase py-2">
                  Summary Sheet – Candidate Eligibility Criteria for Review (compulsory to fill)
                </th>
              </tr>
              <tr style={{ backgroundColor: "#e1e1e1" }}>
                <th className="pdf-label text-center py-1.5 w-[30%] border border-black font-extrabold">Criteria</th>
                <th className="pdf-label text-center py-1.5 w-[50%] border border-black font-extrabold">Requirement</th>
                <th className="pdf-label text-center py-1.5 w-[20%] border border-black font-extrabold">Remarks</th>
              </tr>
            </thead>
            <tbody>
              
              {/* Row 1: Education */}
              <tr style={{ backgroundColor: "#fff2cc" }}>
                <td className="font-bold p-2.5 border border-black">Education</td>
                <td className="p-2.5 border border-black font-semibold">Must be a graduate (full-time; part-time not acceptable)</td>
                <td className="p-0 border border-black">
                  <input
                    type="text"
                    className="cell-input text-center"
                    value={fields.eduRemarks}
                    onChange={(e) => handleChange("eduRemarks", e.target.value)}
                  />
                </td>
              </tr>

              {/* Row 2: Experience */}
              <tr style={{ backgroundColor: "#deebf7" }}>
                <td className="font-bold p-2.5 border border-black">Total Work Experience</td>
                <td className="p-2.5 border border-black font-semibold">Minimum 5 years of overall work experience</td>
                <td className="p-0 border border-black">
                  <input
                    type="text"
                    className="cell-input text-center"
                    value={fields.workExpRemarks}
                    onChange={(e) => handleChange("workExpRemarks", e.target.value)}
                  />
                </td>
              </tr>

              {/* Row 3: Asset Sales */}
              <tr>
                <td className="font-bold p-2.5 border border-black">Asset Sales Experience</td>
                <td className="p-2.5 border border-black font-semibold">At least 2 years in asset sales</td>
                <td className="p-0 border border-black">
                  <input
                    type="text"
                    className="cell-input text-center"
                    value={fields.assetSalesRemarks}
                    onChange={(e) => handleChange("assetSalesRemarks", e.target.value)}
                  />
                </td>
              </tr>

              {/* Row 4: Alternative Sales */}
              <tr style={{ backgroundColor: "#e2f0d9" }}>
                <td className="font-bold p-2.5 border border-black">Alternative Sales Experience</td>
                <td className="p-2.5 border border-black font-semibold">
                  If there are no asset sales, minimum 7 years in other sales (insurance, FMCG, etc.)
                </td>
                <td className="p-0 border border-black">
                  <input
                    type="text"
                    className="cell-input text-center"
                    value={fields.altSalesRemarks}
                    onChange={(e) => handleChange("altSalesRemarks", e.target.value)}
                  />
                </td>
              </tr>

              {/* Row 5: Team Mgmt */}
              <tr style={{ backgroundColor: "#f2f2f2" }}>
                <td className="font-bold p-2.5 border border-black">Team Management</td>
                <td className="p-2.5 border border-black font-semibold">Must have team management experience (Year & Size)</td>
                <td className="p-0 border border-black">
                  <input
                    type="text"
                    className="cell-input text-center"
                    value={fields.teamMgmtRemarks}
                    onChange={(e) => handleChange("teamMgmtRemarks", e.target.value)}
                  />
                </td>
              </tr>

              {/* Row 6: Channel Mgmt */}
              <tr style={{ backgroundColor: "#fce4d6" }}>
                <td className="font-bold p-2.5 border border-black">Channel Management</td>
                <td className="p-2.5 border border-black font-semibold">Must have channel management experience (Year)</td>
                <td className="p-0 border border-black">
                  <input
                    type="text"
                    className="cell-input text-center"
                    value={fields.channelMgmtRemarks}
                    onChange={(e) => handleChange("channelMgmtRemarks", e.target.value)}
                  />
                </td>
              </tr>

            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}
